import React, { useState, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomTextInput from '@/components/CustomTextInput'
import CustomButton from '@/components/CustomButton'
import { CardActionType } from '../../index.type'
import CountryCodeSelector from '@/components/CountryCodeSelector'
import { useSendCode } from '@/hooks/useSendCode'

export default React.memo(function NewAccount() {
    const navigate = useNavigate()
    const { state } = useLocation()
    console.log('NewAccount',state)
    const { t } = useTranslation()
    const [countryCode, setCountryCode] = useState('+86')
    const { sendCode, sendCodeLoading } = useSendCode()
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [errors, setErrors] = useState<{ email?: string; phone?: string }>({})


    // 获取当前操作类型
    const changeType = state?.changeType as CardActionType
    console.log('changeType', changeType)

    // 邮箱验证函数
    const validateEmail = useCallback((email: string): string => {
        if (!email) {
            return t('请输入邮箱地址')
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            return t('请输入有效的邮箱地址')
        }
        return ''
    }, [t])

    // 手机号验证函数（不包含区号）
    const validatePhone = useCallback((phone: string): string => {
        if (!phone) {
            return t('请输入手机号')
        }
        // 验证纯数字手机号：6-15位数字
        const phoneRegex = /^\d{6,15}$/
        if (!phoneRegex.test(phone)) {
            return t('请输入有效的手机号6至15位数字')
        }
        return ''
    }, [t])

    // 处理邮箱输入变化
    const handleEmailChange = useCallback((value: string) => {
        setEmail(value)
        // 清除之前的错误信息
        if (errors.email) {
            setErrors(prev => ({ ...prev, email: undefined }))
        }
        // 实时验证
        const error = validateEmail(value)
        if (error) {
            setErrors(prev => ({ ...prev, email: error }))
        }
    }, [errors.email, validateEmail])

    // 处理手机号输入变化
    const handlePhoneChange = useCallback((value: string) => {
        // 只允许输入数字
        const numericValue = value.replace(/[^\d]/g, '')

        setPhone(numericValue)
        // 清除之前的错误信息
        if (errors.phone) {
            setErrors(prev => ({ ...prev, phone: undefined }))
        }
        // 实时验证
        const error = validatePhone(numericValue)
        if (error) {
            setErrors(prev => ({ ...prev, phone: error }))
        }
    }, [errors.phone, validatePhone])

    // 检查表单是否有效
    const isFormValid = useMemo(() => {
        if (changeType === CardActionType.Email) {
            return email && !validateEmail(email)
        } else if (changeType === CardActionType.Phone) {
            return phone && !validatePhone(phone)
        }
        return false
    }, [changeType, email, phone, validateEmail, validatePhone])

    // 提交验证函数
    const handleSubmit = useCallback(async () => {
        if (changeType === CardActionType.Email) {
            const emailError = validateEmail(email)
            if (emailError) {
                setErrors(prev => ({ ...prev, email: emailError }))
                return
            }
            // 发送验证码
            const data = await sendCode({
                account: email,
                scene: 'change_account'
            })
            if (data) {
                navigate('/change-account/ver-new-account-code', {
                    state: {
                        newAccount: email,
                        ...state
                    }
                })
            }

        } else if (changeType === CardActionType.Phone) {
            const phoneError = validatePhone(phone)
            if (phoneError) {
                setErrors(prev => ({ ...prev, phone: phoneError }))
                return
            }
            const data = await sendCode({
                account: phone,
                scene: 'change_account'
            })
            if (data) {
                navigate('/change-account/ver-new-account-code', {
                    state: {
                        newAccount: phone,
                        ...state
                    }
                })
            }

        }
    }, [changeType, email, phone, validateEmail, validatePhone])

    // 邮箱输入框组件
    const EmailInputView = useMemo(() => (
        <div className="space-y-2">
            <div className="text-sm text-[#1A1A1A]/60 mb-1">{t('请输入新的邮箱')}</div>
            <CustomTextInput
                isErrored={Boolean(errors.email)}
                value={email}
                onChangeText={handleEmailChange}
                placeholder={t('请输入新的邮箱')}
                inputMode='email'
            />
            {errors.email && (
                <div className="text-red-500 text-xs mt-1">{errors.email}</div>
            )}
        </div>
    ), [email, errors.email, handleEmailChange, t])

    // 手机号输入框组件
    const PhoneInputView = useMemo(() => (
        <div className="space-y-2">
            <div className="text-sm text-[#1A1A1A]/60 mb-1">{t('请输入你的新手机号')}</div>
            <div className='flex justify-between items-center'>
                {/* <div className='w-20 mr-2'>
                    <CountryCodeSelector
                        value={countryCode}
                        onChange={setCountryCode}
                    />
                </div> */}
                <div className='flex-1'>
                    <CustomTextInput
                        isErrored={Boolean(errors.phone)}
                        value={phone}
                        onChangeText={handlePhoneChange}
                        placeholder={t('请输入你的新手机号')}
                        inputMode='numeric'
                    />
                </div>

            </div>
            {errors.phone && (
                <div className="text-red-500 text-xs mt-1">{errors.phone}</div>
            )}
        </div>
    ), [phone, errors.phone, handlePhoneChange, t])

    // 根据changeType确定页面标题
    const getPageTitle = () => {
        switch (changeType) {
            case CardActionType.Email:
                return t('新邮箱')
            case CardActionType.Phone:
                return t('新手机号')
            default:
                return t('新账户')
        }
    }

    // 根据changeType确定按钮文本
    const getButtonText = () => {
        switch (changeType) {
            case CardActionType.Email:
                return t('确认新邮箱')
            case CardActionType.Phone:
                return t('确认新手机号')
            default:
                return t('确认')
        }
    }

    return (
        <PageLayout>
            <Header
                leftChildren={<Back />}
                centerChildren={<div className="text-3.75 font-medium text-black">{getPageTitle()}</div>}
            />
            <div className="px-6 py-6">
                <div className="space-y-6">
                    <div className="space-y-4">
                        {changeType === CardActionType.Email && EmailInputView}
                        {changeType === CardActionType.Phone && PhoneInputView}
                    </div>

                    <CustomButton
                        onClick={handleSubmit}
                        loading={sendCodeLoading}
                        disabled={!isFormValid || sendCodeLoading}
                        className={`w-full ${!isFormValid ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {getButtonText()}
                    </CustomButton>
                </div>
            </div>
        </PageLayout>
    )
})